import React from "react";
import ServicePageLayout, { H2, P, UL } from "./ServicePageLayout";

export const meta = {
  title: "Point-to-Point Wireless Alberta | Wireless Bridge Installation | Epicom Systems",
  metaDescription:
    "Point-to-point (PtP) and point-to-multipoint (PtMP) wireless links for building-to-building networking, farms, industrial sites and rural infrastructure across Southern Alberta.",
  canonicalPath: "/point-to-point-wireless",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/point-to-point-wireless#service",
    "name": "Point-to-Point Wireless Installation",
    "serviceType": "Point-to-point and point-to-multipoint wireless network links",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": { "@type": "AdministrativeArea", "name": "Southern Alberta" },
    "description":
      "Point-to-point (PtP) and point-to-multipoint (PtMP) wireless links across Southern Alberta — connect separate buildings, farm sites and remote infrastructure with high-throughput licensed and unlicensed wireless bridges.",
    "url": "https://epicom.ca/point-to-point-wireless",
  },
};

export default function PointToPointWireless() {
  return (
    <ServicePageLayout
      title={meta.title}
      metaDescription={meta.metaDescription}
      canonicalPath={meta.canonicalPath}
      overline="Point-to-Point Wireless"
      h1="Wireless Bridges & Building-to-Building Links"
      intro="When trenching fibre isn’t practical — because the sites are too far apart, cross a road, span a coulee or belong to a customer who’d rather not tear up their yard — a properly-engineered wireless bridge gets the network across the gap. Epicom has installed 200+ of these links across Southern Alberta."
      jsonLd={meta.jsonLd}
      relatedLinks={[
        { to: "/unifi-installation", label: "UniFi Installation" },
        { to: "/business-wifi", label: "Business Wi-Fi" },
        { to: "/cellular-das", label: "Cellular DAS" },
      ]}
    >
      <H2>Common use cases</H2>
      <UL>
        <><strong className="text-white/90">Building-to-building networking</strong> — extend one LAN across multiple buildings on the same site without pulling cable underground.</>
        <><strong className="text-white/90">Farms, feedlots and acreages</strong> — get network and internet from the house or main shop out to the barn, the shop across the yard, the pivot, or the far end of the operation.</>
        <><strong className="text-white/90">Multi-site commercial operations</strong> — link an office to a shop, a warehouse to a yard, or a main site to a secondary building without waiting on a fibre install.</>
        <><strong className="text-white/90">Remote cameras and infrastructure</strong> — put an IP camera, gate controller or SCADA endpoint out where cable can’t reach, backhauled over wireless.</>
        <><strong className="text-white/90">Solar and industrial sites</strong> — link inverters, controllers or monitoring gear back to a central network for real-time visibility.</>
        <><strong className="text-white/90">Point-to-multipoint distribution</strong> — one base station serving several remote sites, common for multi-site farm operations.</>
      </UL>

      <H2>What actually goes into a good link</H2>
      <P>
        Every link comes down to line of sight, mounting, and gear selection. Line of sight
        gets planned before the truck rolls — path profile, height needed, obstructions,
        Fresnel-zone clearance. Mounting matters more than most people realize; a
        good radio on a wobbly mast doesn’t stay aligned for long. We install on real
        structures with real hardware, not zip-ties on a fence post.
      </P>
      <P>
        Radio selection depends on distance, throughput required, spectrum available and
        interference in the area. A 500 m yard-to-shop link, a 5 km cross-section link and a
        20 km multi-site link are three different products with three different price points
        — we quote what the job actually needs rather than over- or under-buying.
      </P>

      <H2>Where PtP wireless does not fit</H2>
      <P>
        PtP wireless isn’t a magic wand. It needs clear line of sight, it doesn’t punch
        through mature bush or hills, and it doesn’t replace fibre for extreme-throughput
        campus backbones. When we look at a site and the answer is “this is really a fibre
        job,” we’ll tell you.
      </P>

      <H2>Track record</H2>
      <P>
        Epicom has deployed 200+ point-to-point and point-to-multipoint wireless links across
        Southern Alberta — farms, businesses, industrial sites, remote SCADA and solar
        installations. Most of those are still online, still aligned and still doing their
        job years later. If you have a site you can’t get network to, there’s a good chance
        we’ve solved something similar.
      </P>
    </ServicePageLayout>
  );
}
